// 公共串口层：封装 Web Serial 读写、协议组帧、拆包及异常处理。
// 协议字段：5A 3C（帧头）| LEN（长度）| CMD | SEQ | DATA | SUM | DB（帧尾）
class SerialPortCommon {
  constructor() {
    this.port = null;
    this.reader = null;
    this.writer = null;
    this.connected = false;
    this.connecting = null;
    this.listening = false;
    this.buffer = [];
    this.listeners = new Set();
    this.sequence = 0;
  }
  addDataListener(fn) {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }
  removeDataListener(fn) {
    this.listeners.delete(fn);
  }
  async connect(options = {}) {
    if (!("serial" in navigator))
      throw new Error("浏览器不支持 Web Serial API");
    if (this.connected) return true;
    if (this.connecting) return this.connecting;
    this.connecting = (async () => {
      let ports = [];
      if (typeof navigator.serial.getPorts === "function") {
        try {
          ports = await navigator.serial.getPorts();
        } catch (e) {}
      }
      let savedInfo = null;
      try {
        savedInfo = JSON.parse(
          localStorage.getItem("jm-serial-port-info") || "null"
        );
      } catch (e) {}
      const match = (port) => {
        if (!savedInfo) return false;
        const info = port.getInfo ? port.getInfo() : {};
        return (
          (!savedInfo.usbVendorId ||
            info.usbVendorId === savedInfo.usbVendorId) &&
          (!savedInfo.usbProductId ||
            info.usbProductId === savedInfo.usbProductId)
        );
      };
      this.port = ports.find(match) || ports[0] || null;
      if (!this.port) this.port = await navigator.serial.requestPort();
      console.log("[JM串口] 正在连接", this.port);
      const portInfo = this.port.getInfo ? this.port.getInfo() : {};
      console.info("[JM串口] 已选择设备", portInfo);
      try {
        await this.port.open(
          Object.assign(
            { baudRate: 9600, dataBits: 8, stopBits: 1, parity: "none" },
            options
          )
        );
      } catch (error) {
        const message =
          error && error.name === "NetworkError"
            ? "串口打开失败：该串口已被其他程序占用，请关闭串口工具后重试"
            : error.message;
        this.port = null;
        throw new Error(message);
      }
      this.connected = true;
      try {
        localStorage.setItem("jm-serial-port-info", JSON.stringify(portInfo));
      } catch (e) {}
      console.info("[JM串口] 连接成功", {
        baudRate: 9600,
        dataBits: 8,
        stopBits: 1,
        parity: "none",
      });
      this._listen();
      return true;
    })();
    try {
      return await this.connecting;
    } finally {
      this.connecting = null;
    }
  }
  async disconnect() {
    if (!this.port && !this.connected) return;
    console.info("[JM串口] 正在断开");
    this.listening = false;
    try {
      if (this.reader) {
        await this.reader.cancel();
        this.reader.releaseLock();
      }
    } catch (e) {}
    try {
      if (this.writer) this.writer.releaseLock();
    } catch (e) {}
    if (this.port && this.port.readable)
      try {
        await this.port.close();
      } catch (e) {}
    this.reader = this.writer = this.port = null;
    this.connected = false;
    this.buffer = [];
    console.info("[JM串口] 已断开");
  }
  async sendCommand(command) {
    if (!this.connected) throw new Error("串口未连接");
    // JM callers pass a complete protocol frame as a hexadecimal string.
    // Numeric commands remain supported for the cancel/legacy helpers.
    const frame = this.hexToBytes(command);
    console.info(
      `[JM串口] 发送(${frame.length}字节): ${this.bytesToHex(frame)}`
    );
    this.writer = this.port.writable.getWriter();
    try {
      await this.writer.write(frame);
    } finally {
      this.writer.releaseLock();
      this.writer = null;
    }
    return frame;
  }
  calculateSum(bytes, direction = "") {
    // 协议 SUM：从 LEN 后开始累加至校验位之前。
    // 当前调用数组包含 LEN，因此跳过第一个字节；最后只保留累加和低 8 位。
    const sumBytes = Array.from(bytes).slice(1);
    const total = sumBytes.reduce((result, value) => result + value, 0);
    const checksum = total & 0xff;
    console.debug(`[JM串口] ${direction}SUM计算`, {
      bytes: this.bytesToHex(sumBytes),
      total,
      totalHex: `0x${total.toString(16).toUpperCase()}`,
      lowByte: `0x${this.bytesToHex([checksum])}`,
    });
    return checksum;
  }
  hexToBytes(hex) {
    const value = hex.replace(/\s+/g, "");
    if (!value || value.length % 2 !== 0 || !/^[0-9a-f]+$/i.test(value)) {
      throw new Error("无效的十六进制串口指令");
    }
    const bytes = new Uint8Array(value.length / 2);
    for (let i = 0; i < value.length; i += 2) {
      bytes[i / 2] = parseInt(value.slice(i, i + 2), 16);
    }
    return bytes;
  }
  bytesToHex(bytes) {
    return Array.from(bytes)
      .map((value) => value.toString(16).padStart(2, "0").toUpperCase())
      .join(" ");
  }
  parsePacket(bytes) {
    const a = Array.from(bytes);
    console.debug(`[JM串口] 接收 (${a.length}字节): ${this.bytesToHex(a)}`);
    if (a[0] !== 0x5a || a[1] !== 0x3c || a[a.length - 1] !== 0xdb) {
      console.warn(`[JM串口] 丢弃非法帧 ${this.bytesToHex(a)}`);
      return null;
    }
    const len = a[2];
    if (a.length !== len + 5) {
      console.warn(`[JM串口] 帧长度错误，声明=${len}，实际=${a.length - 5}`);
      return null;
    }
    const sum = this.calculateSum(a.slice(2, -2), "接收");
    if (sum !== a[a.length - 2]) {
      console.warn(
        `[JM串口] 校验失败，计算${this.bytesToHex([
          sum,
        ])}，收到=${this.bytesToHex([a[a.length - 2]])}`
      );
      return null;
    }
    // POS response: CMD | RETURN_CODE | SEQ | DATA...
    return {
      length: len,
      command: a[3],
      returnCode: a[4],
      sequence: a[5],
      data: a.slice(6, -2),
      raw: a,
    };
  }
  async _listen() {
    if (this.listening) return;
    this.listening = true;
    while (this.listening && this.port && this.port.readable) {
      this.reader = this.port.readable.getReader();
      try {
        while (true) {
          const { value, done } = await this.reader.read();
          if (done) break;
          if (value) {
            console.debug(
              `[JM串口] 接收分片 (${value.length}字节): ${this.bytesToHex(
                value
              )}`
            );
            this.buffer.push(...value);
          }
          this._parseBuffer();
        }
      } catch (e) {
        if (this.listening) console.error("串口读取异常", e);
      } finally {
        try {
          this.reader.releaseLock();
        } catch (e) {}
        this.reader = null;
      }
    }
  }
  _parseBuffer() {
    while (this.buffer.length >= 5) {
      const i = this.buffer.indexOf(0x5a);
      if (i < 0) {
        console.warn(
          `[JM串口] 未找到帧头，丢弃 (${
            this.buffer.length
          }字节): ${this.bytesToHex(this.buffer)}`
        );
        this.buffer = [];
        return;
      }
      if (i > 0) {
        const discarded = this.buffer.splice(0, i);
        console.warn(`[JM串口] 丢弃帧头前数据 ${this.bytesToHex(discarded)}`);
      }
      const len = this.buffer[2];
      const total = len + 5;
      if (this.buffer.length < total) return;
      const raw = this.buffer.splice(0, total);
      const packet = this.parsePacket(raw);
      if (packet) {
        console.info(
          `[JM串口] 返回 (${raw.length}字节): ${this.bytesToHex(raw)}`,
          {
            command: `0x${this.bytesToHex([packet.command])}`,
            returnCode: `0x${this.bytesToHex([packet.returnCode])}`,
            sequence: packet.sequence,
          }
        );
        this.listeners.forEach((fn) => fn(packet));
      }
    }
  }
  async cancelTransaction() {
    // return this.sendCommand(0x03, new Array(12).fill(0));
  }
}
export default new SerialPortCommon();
